import * as React from 'react'
import ReactDOM from 'react-dom'
import {ArrUtil, logger, Util} from 'yougile-util'
import {
  DataObjectId,
  Entity,
  Hub,
  rootNodeId,
  SandboxTree,
  Tag,
  User
} from 'yougile-model'
import {UtilUi} from '../../common/utils-ui'
import {Api} from '../api/api'
import {Item} from '../api/model/item'
import {Runner} from '../api/runner'
import {Sync} from '../client/sync'
import {showConfirm} from './common/base/notifications'
import {UserAvatar} from './user-avatar'
import {UserName} from './user-name'

interface State {
  entity?: Entity
  box?: SandboxTree
  shownItemId?: DataObjectId
  search?: string
}

type MouseEvent = React.MouseEvent

export class RepoNavigator extends React.Component<unknown, State> {
  private static element: Element
  private static loggedVarsCount = 0

  private static toggle(): void {
    let el = document.getElementById('repo-navigator')
    if (el) {
      UtilUi.removeEl(el)
    } else {
      el = document.createElement('div')
      el.id = 'repo-navigator'
      document.body.appendChild(el)
      RepoNavigator.element = el

      ReactDOM.render(<RepoNavigator />, el)
    }
  }

  public static init(): void {
    if (RepoNavigator.element) {
      throw new Error('Already initialized')
    }
    window.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.keyCode === 220 && event.ctrlKey) {
        // ctrl + \
        RepoNavigator.toggle()
      }
    })
  }

  private moreInfoClick(obj: Entity): void {
    const varName = 'res' + RepoNavigator.loggedVarsCount
    RepoNavigator.loggedVarsCount++
    logger.log(varName + ' =', obj)
    ;(window as any)[varName] = obj
    if (this.state.shownItemId === obj.id) {
      this.setState({shownItemId: undefined})
    } else {
      this.setState({shownItemId: obj.id})
    }
  }

  constructor() {
    super({})
    const box = Sync.getCurrentSandbox()
    this.state = {
      box,
      entity: undefined,
      shownItemId: undefined,
      search: ''
    }
  }

  public goUp(): void {
    if (!this.state.entity || this.state.entity.id === rootNodeId) {
      return
    }
    if (this.state.entity.location === rootNodeId) {
      this.setState({entity: undefined})
    } else {
      const entity = this.state.box?.get(this.state.entity.location)
      if (!entity) {
        return
      }
      this.setState({entity})
    }
  }

  public onSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const text = event?.currentTarget?.value
    if (text === undefined) {
      return
    }
    if (text !== this.state.search) {
      this.setState({...this.state, search: text})
    }
  }

  public static sort(a: Entity, b: Entity): number {
    const aGoesFirst = -1
    const bGoesFirst = 1
    if (!a) {
      return aGoesFirst
    } else if (!b) {
      return bGoesFirst
    }
    if (a.deleted && !b.deleted) {
      return bGoesFirst
    }
    if (b.deleted && !a.deleted) {
      return aGoesFirst
    }
    if (a.dataType !== b.dataType) {
      const ord = ['User', 'Hub', 'Tag']
      return ord.indexOf(a.dataType) - ord.indexOf(b.dataType)
    }
    if (a.title.startsWith('.') && !b.title.startsWith('.')) {
      return aGoesFirst
    }
    if (!a.title.startsWith('.') && b.title.startsWith('.')) {
      return bGoesFirst
    }
    return a.timestamp < b.timestamp ? aGoesFirst : 0
  }

  public renderEntity(entity: Entity): JSX.Element {
    if (entity instanceof User) {
      return (
        <div className="rn-child-name">
          <UserAvatar user={entity} />
          <UserName user={entity} />
        </div>
      )
    }
    return (
      <div className="rn-child-name">
        {entity.dataType === 'Hub' ? (
          <i className="fa fa-folder-o" />
        ) : entity.dataType === 'Tag' ? (
          '#'
        ) : (
          '@'
        )}{' '}
        {entity.title}
      </div>
    )
  }

  // eslint-disable-next-line max-lines-per-function
  public override render(): React.ReactNode {
    if (!this.state.box) {
      return undefined
    }

    const classFromEntity = (entity: Entity) =>
      'rn-child' +
      (entity.deleted ? ' deleted' : '') +
      (entity.id === this.state.shownItemId ? ' shown' : '')
    const id = this.state.entity ? this.state.entity.id : rootNodeId
    let entities = [...this.state.box.getChildrenIds(id)].map(
      (eId: DataObjectId) => this.state.box?.get(eId)
    )
    if (this.state.entity instanceof Tag || this.state.entity instanceof User) {
      entities = [...this.state.box.forwardConnections(id)].map(
        this.state.box.get
      )
    }

    const moreInfo = (ent: Entity) => {
      if (!this.state.box) {
        return []
      }
      if (this.state.shownItemId !== ent.id) {
        return []
      } else {
        return [
          <div
            className="rn-child-info"
            key={1}
            onClick={(e: MouseEvent) => e.stopPropagation()}
          >
            <table className="table">
              <tbody>
                <tr>
                  <td>id</td>
                  <td>{ent.id}</td>
                </tr>
                <tr>
                  <td>type</td>
                  <td>{new Item(ent.id, this.state.box).type}</td>
                </tr>
                <tr>
                  <td>name</td>
                  <td>{new Item(ent.id, this.state.box).name}</td>
                </tr>
                <tr>
                  <td>timestamp</td>
                  <td>{ent.timestamp}</td>
                </tr>
                <tr>
                  <td>deleted</td>
                  <td>{ent.deleted ? 'true' : 'false'}</td>
                </tr>
                <tr>
                  <td>lazy</td>
                  <td>{ent.isLazy ? 'true' : 'false'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ]
      }
    }

    const children = ArrUtil.defined([...entities])
      .filter(
        ent =>
          !this.state.search ||
          ent.title.toLowerCase().includes(this.state.search.toLowerCase())
      )
      .sort(RepoNavigator.sort)
      .map(ent => (
        <div
          className={classFromEntity(ent)}
          key={ent.id}
          onClick={() => {
            if (ent.dataType === 'Hub') {
              this.setState({entity: ent, search: ''})
            }
          }}
        >
          {this.renderEntity(ent)}
          <a
            className="_link rn-child-log"
            onClick={(e: MouseEvent) => {
              e.stopPropagation()
              this.moreInfoClick(ent)
            }}
          >
            [{ent.id !== this.state.shownItemId ? 'show' : 'hide'}]
          </a>
          {moreInfo(ent)}
        </div>
      ))

    const companyOpts = ArrUtil.defined(
      [...Sync.listSandboxes(), Sync.newsSandbox].map((s: SandboxTree) => {
        if (!s?.companyId) {
          return undefined
        }
        const companyHub = s.get(s.companyId)
        if (!companyHub) {
          return undefined
        }

        return (
          <option value={s.companyId} key={s.companyId} className="rn-cmp-opt">
            {companyHub.title}
          </option>
        )
      })
    )

    const rawScripts = this.state.box ? Api.listScripts(this.state.box) : []

    const scripts = rawScripts.map((hub: Hub) => (
      <div
        className={Util.className({
          'rn-script': true,
          disabled: !Api.isEnabledScript(hub)
        })}
        key={hub.id}
      >
        <i className="fa fa-file-code-o" /> {hub.title}
        <a
          className="_link rn-child-log"
          onClick={async () => {
            if (await showConfirm(`Delete “${hub.title}” script?`)) {
              this.state.box?.remove(hub)
              this.forceUpdate()
            }
          }}
        >
          <i className="fa fa-trash-o" />
        </a>
        <a
          className="_link rn-child-log"
          onClick={() => {
            Api.toggleEnableScript(hub)
            this.forceUpdate()
          }}
        >
          [{Api.isEnabledScript(hub) ? 'disable' : 'enable'}]
        </a>
        <a
          className="_link rn-child-log"
          onClick={() => {
            Runner.unloadJs(hub.title, hub.companyId)
            Runner.runJs(hub.description, hub.title, hub.companyId, undefined)
          }}
        >
          [reload]
        </a>
      </div>
    ))
    return (
      <div id="repo-navigator" className="overflow-hidden">
        <div className="rn-head">
          <div className="rn-close" onClick={() => RepoNavigator.toggle()}>
            <i className="fa fa-times" />
          </div>
          <div className="rn-cmp">
            <select
              className="rn-cmp-sel"
              value={this.state.box.companyId}
              onChange={(e: React.FormEvent) => {
                const sel = e.target as HTMLSelectElement
                const box = Sync.get({
                  companyId: sel.value as DataObjectId
                })
                this.setState({box, entity: undefined})
              }}
            >
              {companyOpts}
            </select>
          </div>
        </div>
        <div className="rn-children">
          <div className="rn-children-curr z-1">
            <div className="rn-title">
              <i className="fa fa-folder-open-o" />{' '}
              {this.state.entity ? this.state.entity.title : 'Root'}
            </div>
            <a className="_link rn-head-up" onClick={() => this.goUp()}>
              up
            </a>
            <a className="_link rn-head-up" onClick={() => this.forceUpdate()}>
              refresh
            </a>
          </div>

          <div className="rn-children-search z-1">
            <input
              value={this.state.search}
              onChange={this.onSearchChange}
              placeholder={'Search'}
            ></input>
          </div>

          <div className="rn-child-list">{children}</div>
        </div>
        <div className="rn-scripts">
          {scripts}
          <div
            className="rn-script-add _link"
            onClick={() => {
              if (!this.state.box) {
                return
              }

              Api.loadScript(this.state.box, () => this.forceUpdate())
            }}
          >
            <i className="fa fa-plus" /> add a script
          </div>
        </div>
      </div>
    )
  }
}
